class State {
  constructor(id) {
    this.id = id;
    this.transitions = new Map(); // Map of symbol -> array of states
    this.epsilonTransitions = new Set(); // Set of states reachable via epsilon transitions
    this.isAccepting = false;
  }

  addTransition(symbol, state) {
    if (!this.transitions.has(symbol)) {
      this.transitions.set(symbol, new Set());
    }
    this.transitions.get(symbol).add(state);
  }

  addEpsilonTransition(state) {
    this.epsilonTransitions.add(state);
  }

  setAccepting(isAccepting) {
    this.isAccepting = isAccepting;
  }
}

class NFA {
  constructor() {
    this.states = [];
    this.startState = null;
    this.alphabet = new Set();
  }

  createState() {
    const state = new State(this.states.length);
    this.states.push(state);
    return state;
  }

  setStartState(state) {
    this.startState = state;
  }

  // Generowanie NFA z drzewa wyprowadzeń
  static fromDerivationTree(tree) {
    const builder = new NFABuilder();
    return builder.buildFromTree(tree);
  }

  // Sprawdzanie, czy słowo jest akceptowane przez automat
  test(input) {
    if (!this.startState) {
      return false;
    }

    // Dla słowa pustego sprawdź tylko epsilon-domknięcie stanu początkowego
    if (input === '') {
      const epsilonClosure = this.getEpsilonClosure([this.startState]);
      return Array.from(epsilonClosure).some(state => state.isAccepting);
    }

    // Inicjalizacja zbioru aktualnych stanów jako epsilon-domknięcie stanu początkowego
    let currentStates = this.getEpsilonClosure([this.startState]);
    
    // Przetwarzanie każdego symbolu z wejścia
    for (const symbol of input) {
      // Sprawdzenie, czy symbol należy do alfabetu
      if (!this.alphabet.has(symbol)) {
        return false;
      }
      
      // Przejście do następnych stanów na podstawie bieżącego symbolu
      const nextStates = this.transition(currentStates, symbol);
      currentStates = this.getEpsilonClosure(nextStates);
      
      // Jeśli nie ma możliwych przejść, słowo jest odrzucane
      if (currentStates.size === 0) {
        return false;
      }
    }
    
    // Sprawdzenie, czy którykolwiek ze stanów końcowych jest stanem akceptującym
    return Array.from(currentStates).some(state => state.isAccepting);
  }

  // Wykonywanie przejścia na podstawie symbolu
  transition(states, symbol) {
    const nextStates = new Set();
    
    for (const state of states) {
      if (state.transitions.has(symbol)) {
        const transitions = state.transitions.get(symbol);
        for (const nextState of transitions) {
          nextStates.add(nextState);
        }
      }
    }
    
    return nextStates;
  }

  // Obliczanie epsilon-domknięcia zbioru stanów
  getEpsilonClosure(states) {
    const closure = new Set(states);
    const stack = [...states];
    
    while (stack.length > 0) {
      const state = stack.pop();
      
      for (const nextState of state.epsilonTransitions) {
        if (!closure.has(nextState)) {
          closure.add(nextState);
          stack.push(nextState);
        }
      }
    }
    
    return closure;
  }
}

// Klasa pomocnicza do budowania NFA z drzewa wyprowadzeń
class NFABuilder {
  buildFromTree(tree) {
    const nfa = new NFA();
    
    // Rekurencyjne budowanie NFA z drzewa
    const { start, end } = this.buildFragment(tree, nfa);
    
    nfa.setStartState(start);
    end.setAccepting(true);
    
    return nfa;
  }

  buildFragment(node, nfa) {
    // Tworzenie stanów początkowego i końcowego dla fragmentu
    const startState = nfa.createState();
    const endState = nfa.createState();
    
    // Obsługa różnych typów węzłów drzewa
    if (node.type === 'SYMBOL') {
      // Dodaj przejście dla symbolu
      startState.addTransition(node.value, endState);
      nfa.alphabet.add(node.value);
      return { start: startState, end: endState };
    } else if (node.type === 'regular' || node.type === 'sequence' || node.type === 'element') {
      // Dla węzłów typu regular, sequence lub element, budujemy fragmenty dla dzieci
      if (node.children && node.children.length === 1) {
        return this.buildFragment(node.children[0], nfa);
      } else if (node.children && node.children.length > 1) {
        // Sprawdź, czy to jest konstrukcja specjalna (Kleene, optional, plus)
        if (node.children.some(child => child.type === 'LCURLY') && 
            node.children.some(child => child.type === 'RCURLY')) {
          // Konstrukcja Kleene'ego {a} (0 lub więcej)
          return this.buildKleeneStar(node, nfa);
        } else if (node.children.some(child => child.type === 'LSQBRA') && 
                  node.children.some(child => child.type === 'RSQBRA')) {
          // Konstrukcja opcjonalna [a] (0 lub 1)
          return this.buildOptional(node, nfa);
        } else if (node.children.some(child => child.type === 'LCBRA') && 
                  node.children.some(child => child.type === 'RCBRA')) {
          // Konstrukcja grupująca (a)
          return this.buildGrouping(node, nfa);
        } else {
          // Łączenie sekwencyjne fragmentów
          let currentEnd = null;
          
          for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            
            // Pomiń węzły specjalne, które nie reprezentują części automatu
            if (child.type === 'PIPE' || child.type === 'LCBRA' || child.type === 'RCBRA' || 
                child.type === 'LCURLY' || child.type === 'RCURLY' || child.type === 'LSQBRA' || child.type === 'RSQBRA') {
              continue;
            }
            
            const fragment = this.buildFragment(child, nfa);
            
            if (i === 0) {
              // Pierwszy fragment - połącz start z jego startem
              startState.addEpsilonTransition(fragment.start);
              currentEnd = fragment.end;
            } else {
              // Kolejne fragmenty - połącz poprzedni koniec z bieżącym startem
              currentEnd.addEpsilonTransition(fragment.start);
              currentEnd = fragment.end;
            }
          }
          
          // Połącz ostatni koniec z końcem całego fragmentu
          if (currentEnd) {
            currentEnd.addEpsilonTransition(endState);
            return { start: startState, end: endState };
          } else {
            // Jeśli nie ma dzieci, po prostu dodaj epsilon-przejście
            startState.addEpsilonTransition(endState);
            return { start: startState, end: endState };
          }
        }
      } else {
        // Brak dzieci, dodaj epsilon-przejście
        startState.addEpsilonTransition(endState);
        return { start: startState, end: endState };
      }
    } else if (node.type === 'alternative') {
      // Implementacja alternatywy (|)
      let firstChild = null;
      let thirdChild = null;
      
      // Znajdź fragment przed i po '|'
      node.children.forEach(child => {
        if (child.type === 'PIPE') {
          // Pomiń węzeł '|'
        } else if (firstChild === null) {
          firstChild = child;
        } else {
          thirdChild = child;
        }
      });
      
      if (firstChild && thirdChild) {
        // Buduj fragmenty dla obu części alternatywy
        const leftFragment = this.buildFragment(firstChild, nfa);
        const rightFragment = this.buildFragment(thirdChild, nfa);
        
        // Łączenie fragmentów poprzez epsilon-przejścia
        startState.addEpsilonTransition(leftFragment.start);
        startState.addEpsilonTransition(rightFragment.start);
        
        leftFragment.end.addEpsilonTransition(endState);
        rightFragment.end.addEpsilonTransition(endState);
      } else if (firstChild) {
        // Tylko jeden fragment, bez alternatywy
        const fragment = this.buildFragment(firstChild, nfa);
        startState.addEpsilonTransition(fragment.start);
        fragment.end.addEpsilonTransition(endState);
      } else {
        // Brak dzieci, dodaj epsilon-przejście
        startState.addEpsilonTransition(endState);
      }
      
      return { start: startState, end: endState };
    }
    
    // Domyślnie, jeśli nie ma dopasowania do specjalnych przypadków
    startState.addEpsilonTransition(endState);
    return { start: startState, end: endState };
  }

  // Implementacja domknięcia Kleene'ego (0 lub więcej)
  buildKleeneStar(node, nfa) {
    const startState = nfa.createState();
    const endState = nfa.createState();
    
    // Znajdź fragment wewnątrz nawiasów klamrowych {}
    let innerNode = null;
    
    for (const child of node.children) {
      if (child.type !== 'LCURLY' && child.type !== 'RCURLY') {
        innerNode = child;
        break;
      }
    }
    
    if (innerNode) {
      // Buduj fragment dla zawartości wewnątrz {}
      const fragment = this.buildFragment(innerNode, nfa);
      
      // Implementacja domknięcia Kleene'ego:
      // 1. Przejście epsilon od startu do końca (0 wystąpień)
      startState.addEpsilonTransition(endState);
      
      // 2. Przejście epsilon od startu do fragmentu
      startState.addEpsilonTransition(fragment.start);
      
      // 3. Przejście epsilon od końca fragmentu do startu fragmentu (powtórzenia)
      fragment.end.addEpsilonTransition(fragment.start);
      
      // 4. Przejście epsilon od końca fragmentu do końca całości
      fragment.end.addEpsilonTransition(endState);
    } else {
      // Jeśli nie znaleziono zawartości wewnątrz {}, dodaj epsilon-przejście
      startState.addEpsilonTransition(endState);
    }
    
    return { start: startState, end: endState };
  }

  // Implementacja opcjonalności (0 lub 1)
  buildOptional(node, nfa) {
    const startState = nfa.createState();
    const endState = nfa.createState();
    
    // Znajdź fragment wewnątrz nawiasów kwadratowych []
    let innerNode = null;
    
    for (const child of node.children) {
      if (child.type !== 'LSQBRA' && child.type !== 'RSQBRA') {
        innerNode = child;
        break;
      }
    }
    
    if (innerNode) {
      // Buduj fragment dla zawartości wewnątrz []
      const fragment = this.buildFragment(innerNode, nfa);
      
      // Implementacja opcjonalności:
      // 1. Przejście epsilon od startu do końca (0 wystąpień)
      startState.addEpsilonTransition(endState);
      
      // 2. Przejście epsilon od startu do fragmentu (1 wystąpienie)
      startState.addEpsilonTransition(fragment.start);
      
      // 3. Przejście epsilon od końca fragmentu do końca całości
      fragment.end.addEpsilonTransition(endState);
    } else {
      // Jeśli nie znaleziono zawartości wewnątrz [], dodaj epsilon-przejście
      startState.addEpsilonTransition(endState);
    }
    
    return { start: startState, end: endState };
  }

  // Implementacja grupowania (a)
  buildGrouping(node, nfa) {
    // Znajdź fragment wewnątrz nawiasów ()
    let innerNode = null;
    
    for (const child of node.children) {
      if (child.type !== 'LCBRA' && child.type !== 'RCBRA') {
        innerNode = child;
        break;
      }
    }
    
    if (innerNode) {
      // Po prostu przekazujemy budowę fragmentu dla zawartości wewnątrz ()
      return this.buildFragment(innerNode, nfa);
    } else {
      // Jeśli nie znaleziono zawartości wewnątrz (), tworzymy pusty automat
      const startState = nfa.createState();
      const endState = nfa.createState();
      startState.addEpsilonTransition(endState);
      return { start: startState, end: endState };
    }
  }
}

export default NFA; 